import React from "react";
import { Alert, Box, CircularProgress, Dialog, IconButton, Typography } from "@mui/material";
import CloseIcon from "@mui/icons-material/Close";
import DownloadIcon from "@mui/icons-material/Download";
import LiveRegion from "../../LiveRegion/LiveRegion";
import { FONT_INTER } from "../../../styles/fonts";

const TITLE_ID = "file-viewer-dialog-title";
const IMAGE_TYPES = ["png", "jpeg", "jpg"];

const Viewer = ({ fileName, fileType, fileUrl }) => {
  if (IMAGE_TYPES.includes(fileType)) {
    return (
      <Box
        component="img"
        src={fileUrl}
        alt={`Pré-visualização de ${fileName}`}
        sx={{ maxWidth: "100%", maxHeight: "70vh", display: "block", mx: "auto" }}
      />
    );
  }

  if (fileType === "pdf") {
    return (
      <Box
        component="iframe"
        src={`${fileUrl}#toolbar=0`}
        title={`Visualização de ${fileName}`}
        sx={{ width: "100%", height: "70vh", border: 0 }}
      />
    );
  }

  if (fileType === "mp3") {
    return (
      <audio src={fileUrl} controls aria-label={fileName} style={{ width: "100%" }}>
        Seu navegador não suporta o elemento de áudio.
      </audio>
    );
  }

  if (fileType === "mp4") {
    return (
      <video
        src={fileUrl}
        controls
        aria-label={fileName}
        style={{ width: "100%", maxHeight: "600px" }}
      >
        Seu navegador não suporta o elemento de vídeo.
      </video>
    );
  }

  return (
    <Alert severity="info">
      Este tipo de arquivo não pode ser visualizado aqui. Use o botão de baixar.
    </Alert>
  );
};

const FileViewerDialog = ({
  open = true,
  fileName,
  fileType,
  fileUrl,
  isLoading,
  hasError,
  hideDownload,
  onClose,
  onDownload,
}) => (
  <Dialog
    open={open}
    onClose={onClose}
    fullWidth
    maxWidth="lg"
    aria-labelledby={TITLE_ID}
    PaperProps={{
      "aria-modal": true,
      sx: { borderRadius: "8px", p: 3, minHeight: "400px", fontFamily: FONT_INTER },
    }}
  >
    <LiveRegion />

    <Box
      sx={{
        display: "flex",
        alignItems: "center",
        justifyContent: "space-between",
        gap: 2,
        mb: 3,
      }}
    >
      <Typography
        component="h2"
        id={TITLE_ID}
        sx={{
          fontFamily: "inherit",
          fontSize: "22px",
          fontWeight: 600,
          color: "#3D3D3D",
          minWidth: 0,
          overflowWrap: "anywhere",
        }}
      >
        {fileName || "Visualizar arquivo"}
      </Typography>

      <Box sx={{ display: "flex", alignItems: "center", gap: 1, flexShrink: 0 }}>
        {!!fileUrl && !hideDownload && (
          <IconButton
            onClick={onDownload}
            aria-label={`Baixar ${fileName}`}
            sx={{ width: 48, height: 48 }}
          >
            <DownloadIcon fontSize="small" />
          </IconButton>
        )}

        <IconButton
          onClick={onClose}
          aria-label="Fechar visualização"
          sx={{ width: 48, height: 48 }}
        >
          <CloseIcon fontSize="small" />
        </IconButton>
      </Box>
    </Box>

    {hasError ? (
      <Alert severity="error">Não foi possível carregar o arquivo.</Alert>
    ) : isLoading ? (
      <Box
        role="status"
        sx={{ display: "flex", alignItems: "center", justifyContent: "center", gap: 2, py: 6 }}
      >
        <CircularProgress size={24} aria-hidden="true" />
        <Typography sx={{ fontFamily: "inherit", fontSize: "14px", color: "#666666" }}>
          Carregando arquivo…
        </Typography>
      </Box>
    ) : (
      <Viewer fileName={fileName} fileType={fileType} fileUrl={fileUrl} />
    )}
  </Dialog>
);

export default FileViewerDialog;
